<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card beauty-card">
      <el-form :inline="true" size="small" class="glass-form">
        <el-form-item label="模板名称">
          <el-input v-model="query.name" clearable placeholder="搜索模板" @keyup.enter.native="fetchList" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="fetchList">搜索</el-button>
          <el-button type="primary" plain icon="el-icon-plus" size="mini" @click="handleAdd">新建模板</el-button>
        </el-form-item>
      </el-form>

      <el-table
        v-loading="loading"
        :data="list"
        class="beauty-table"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
      >
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column prop="name" label="模板名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="model" label="预填模型" min-width="120" show-overflow-tooltip />
        <el-table-column prop="prompt" label="提示词" min-width="220" show-overflow-tooltip />
        <el-table-column label="尺寸" width="120" align="center">
          <template slot-scope="{ row }">
            <span v-if="row.resolution">{{ row.resolution }} / {{ row.aspectRatio }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="更新时间" width="170" align="center" />
        <el-table-column label="操作" width="140" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" icon="el-icon-edit" @click="handleEdit(row)">编辑</el-button>
            <el-button type="text" size="mini" icon="el-icon-delete" class="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          :current-page="query.pageNo"
          :page-sizes="[10, 20, 50]"
          :page-size="query.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="v => { query.pageSize = v; query.pageNo = 1; fetchList(); }"
          @current-change="v => { query.pageNo = v; fetchList(); }"
        />
      </div>
    </el-card>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="640px" append-to-body custom-class="beauty-dialog">
      <el-form ref="form" :model="form" :rules="rules" label-width="110px" size="small">
        <el-form-item label="模板名称" prop="name">
          <el-input v-model="form.name" maxlength="100" show-word-limit />
        </el-form-item>
        <el-form-item label="提示词" prop="prompt">
          <el-input v-model="form.prompt" type="textarea" :rows="5" maxlength="2000" show-word-limit />
        </el-form-item>
        <el-form-item label="预填模型">
          <el-input v-model="form.model" placeholder="可选，如 google-nano-banana-2 / doubao-seedream-4.5" />
        </el-form-item>
        <el-form-item label="预填尺寸">
          <el-input v-model="form.resolution" placeholder="如 1k / 2K" style="width: 120px; margin-right: 8px" />
          <el-input v-model="form.aspectRatio" placeholder="如 1:1 / 自适应" style="width: 140px" />
        </el-form-item>
        <el-form-item label="每张数量">
          <el-input-number v-model="form.nPerSource" :min="1" :max="4" />
        </el-form-item>
        <el-form-item label="命名模式">
          <el-radio-group v-model="form.namingMode">
            <el-radio label="derive">衍生命名</el-radio>
            <el-radio label="sequential">顺序编号</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="衍生规则">
          <el-input v-model="form.deriveNameRule" placeholder="YYMMDD-衍生" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  listAiPromptTemplates,
  createAiPromptTemplate,
  updateAiPromptTemplate,
  deleteAiPromptTemplate
} from '@/api/material/aiGenerate';

export default {
  name: 'AiGenerateTemplates',
  data() {
    return {
      loading: false,
      saving: false,
      list: [],
      total: 0,
      query: { pageNo: 1, pageSize: 10, name: '' },
      dialogVisible: false,
      dialogTitle: '新建模板',
      editingId: null,
      form: this.emptyForm(),
      rules: {
        name: [{ required: true, message: '请填写模板名称', trigger: 'blur' }],
        prompt: [{ required: true, message: '请填写提示词', trigger: 'blur' }]
      }
    };
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        prompt: '',
        model: '',
        resolution: '2k',
        aspectRatio: '1:1',
        nPerSource: 1,
        namingMode: 'derive',
        deriveNameRule: 'YYMMDD-衍生',
        remark: ''
      };
    },
    async fetchList() {
      this.loading = true;
      try {
        const res = await listAiPromptTemplates({
          pageNo: this.query.pageNo,
          pageSize: this.query.pageSize,
          name: this.query.name || undefined
        });
        const page = res.data || res;
        this.list = page.records || [];
        this.total = page.total || 0;
      } catch (e) {
        this.$message.error('加载失败');
      } finally {
        this.loading = false;
      }
    },
    handleAdd() {
      this.dialogTitle = '新建模板';
      this.editingId = null;
      this.form = this.emptyForm();
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.dialogTitle = '编辑模板';
      this.editingId = row.id;
      this.form = {
        name: row.name,
        prompt: row.prompt,
        model: row.model || '',
        resolution: row.resolution || '2k',
        aspectRatio: row.aspectRatio || '1:1',
        nPerSource: row.nPerSource || 1,
        namingMode: row.namingMode || 'derive',
        deriveNameRule: row.deriveNameRule || 'YYMMDD-衍生',
        remark: row.remark || ''
      };
      this.dialogVisible = true;
    },
    submit() {
      this.$refs.form.validate(async (ok) => {
        if (!ok) return;
        this.saving = true;
        try {
          if (this.editingId) {
            await updateAiPromptTemplate(this.editingId, this.form);
          } else {
            await createAiPromptTemplate(this.form);
          }
          this.$message.success('保存成功');
          this.dialogVisible = false;
          this.fetchList();
        } catch (e) {
          // handled
        } finally {
          this.saving = false;
        }
      });
    },
    handleDelete(row) {
      this.$confirm(`确认删除模板「${row.name}」？`, '提示', { type: 'warning' })
        .then(async () => {
          await deleteAiPromptTemplate(row.id);
          this.$message.success('已删除');
          this.fetchList();
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.pagination-container {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.danger {
  color: #f56c6c;
}
</style>
